import { ChangeDetectorRef, Component, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Ability, subject } from '@casl/ability';
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/shared/components';
import { CommonService } from 'src/app/shared/services/common.service';
import { ProjectsService } from 'src/app/modules/projects/core/projects.service';

const POLL_INTERVAL_MS = 30000;
const IN_PROGRESS_STATUSES = ['pending', 'building'];

@Component({
    selector: 'app-project-archive',
    templateUrl: './project-archive.component.html',
    styleUrls: ['./project-archive.component.scss'],
    standalone: false
})
export class ProjectArchiveComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId: string;

  canArchive = false;
  loading = false;
  building = false;
  downloading = false;
  archives: any[] = [];
  latest: any = null;

  private pollSub: Subscription | null = null;
  private unsubscribeAbility: (() => void) | null = null;
  private readonly onDestroy: Subject<void> = new Subject<void>();

  constructor(
    private ability: Ability,
    private dialog: MatDialog,
    private zone: NgZone,
    private cdRef: ChangeDetectorRef,
    public commonService: CommonService,
    private projectsService: ProjectsService,
  ) {}

  ngOnInit(): void {
    this.canArchive = this.ability.can('create', subject('ProjectArchive', { project_id: this.projectId }));
    if (this.canArchive) {
      this.loading = true;
      this.loadArchives();
    }

    // CASL rules can finish syncing after this component initializes (e.g. right after
    // login), which would otherwise leave the section hidden for good. Re-evaluate on the
    // ability's own change stream, matching the async-init re-check pattern in
    // project-details-screen.component.ts:81-95.
    this.unsubscribeAbility = this.ability.on('updated', () => {
      const canArchive = this.ability.can('create', subject('ProjectArchive', { project_id: this.projectId }));
      if (canArchive === this.canArchive) {
        return;
      }
      this.canArchive = canArchive;
      if (this.canArchive && !this.archives.length && !this.loading) {
        this.loading = true;
        this.loadArchives();
      }
      if (!this.canArchive) {
        this.stopPolling();
      }
      this.cdRef.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Angular reuses this component instance when navigating between two projects on the
    // same settings sub-tab route (e.g. project 215's Archive tab -> project 216's), so a
    // projectId re-bind can arrive well after ngOnInit. Without this, the previous
    // project's archive list/state would keep rendering under the new project.
    if (!changes.projectId || changes.projectId.firstChange) {
      return;
    }

    this.stopPolling();
    this.archives = [];
    this.latest = null;
    this.building = false;
    this.loading = false;

    this.canArchive = this.ability.can('create', subject('ProjectArchive', { project_id: this.projectId }));
    if (this.canArchive) {
      this.loading = true;
      this.loadArchives();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.unsubscribeAbility?.();
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  get isBuilding(): boolean {
    return this.building || IN_PROGRESS_STATUSES.includes(this.latest?.status);
  }

  // Recomputed on every change-detection pass (no extra timer) — the 30s poll's
  // zone.run(() => this.loadArchives()) already triggers CD while building/pending.
  get buildElapsed(): string | null {
    if (!this.latest?.created_at) {
      return null;
    }
    return moment(this.latest.created_at).fromNow();
  }

  // `latest.progress` is null before the build's first heartbeat and null again once
  // ready/failed, so it's only meaningful mid-build.
  get hasProgress(): boolean {
    return !!(this.latest?.progress && this.latest.progress.total > 0);
  }

  get progressPercent(): number {
    const progress = this.latest?.progress;
    if (!progress || !progress.total) {
      return 0;
    }
    return Math.min(100, Math.round((progress.done / progress.total) * 100));
  }

  get progressSummary(): string {
    const progress = this.latest?.progress;
    if (!progress) {
      return '';
    }
    const items = `${progress.done} of ${progress.total} items`;
    return this.buildElapsed ? `${items} · started ${this.buildElapsed}` : items;
  }

  get buildButtonLabel(): string {
    return this.latest && (this.latest.status === 'failed' || this.latest.status === 'expired')
      ? 'Rebuild archive'
      : 'Build archive';
  }

  // Ready archives newer than 24h can't be rebuilt from here — a rebuild request
  // within that window just points the user back at the same file (recent_archive_exists).
  get isWithinRebuildCooldown(): boolean {
    if (this.latest?.status !== 'ready' || !this.latest?.created_at) {
      return false;
    }
    return moment().diff(moment(this.latest.created_at), 'hours') < 24;
  }

  get countsEntries(): { section: string; included: number; failed: number; configIssues: number }[] {
    const counts = this.latest?.counts || {};
    return Object.entries(counts).map(([section, value]: [string, any]) => ({
      section,
      included: value?.included || 0,
      failed: value?.failed || 0,
      configIssues: value?.configIssues || 0,
    }));
  }

  formatBytes(bytes: number): string {
    if (!bytes && bytes !== 0) {
      return '';
    }
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  }

  buildArchive(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Build project archive',
        'This will build a zip containing all submitted daily reports, approved change orders, pay apps and documents for this project. You will receive an email with a download link when it is ready.',
        'Build',
        'Cancel',
      ),
    });

    dialogRef.afterClosed().pipe(takeUntil(this.onDestroy)).subscribe(confirmed => {
      if (!confirmed) {
        return;
      }

      this.building = true;
      this.projectsService.requestProjectArchive(this.projectId)
        .pipe(takeUntil(this.onDestroy))
        .subscribe({
          next: (archive: any) => {
            this.building = false;
            // Defensive: handle a conflict code even if it ever arrives on a
            // non-throwing response, the same way the error path below does.
            if (archive?.code) {
              this.handleBuildConflict(archive);
              return;
            }
            this.latest = archive;
            this.archives = [archive, ...this.archives];
            this.startPolling();
          },
          error: (error: HttpErrorResponse) => {
            this.building = false;
            if (error?.status === 409) {
              this.handleBuildConflict(error.error);
            } else {
              this.commonService.notification(error?.error?.error || 'Failed to build archive', 'danger');
            }
          },
        });
    });
  }

  private handleBuildConflict(body: any): void {
    const code = body?.code;
    // The 409 body's `archive` field is the raw serializer envelope ({status,data,type}),
    // not the record itself — verified live: POST .../archive -> 409 recent_archive_exists.
    const archiveRecord = body?.archive?.data;

    if (code === 'recent_archive_exists' && archiveRecord) {
      this.latest = archiveRecord;
      this.archives = [archiveRecord, ...this.archives.filter(a => a.id !== archiveRecord.id)];
      this.stopPolling();
      this.commonService.notification('A recent archive already exists — download it below.', 'success');
      return;
    }

    if (code === 'org_limit_reached') {
      this.commonService.notification(
        'Your organization already has two archive builds running — try again once they finish.',
        'danger',
      );
      return;
    }

    // 'archive_in_progress' or a legacy/unspecified 409 body.
    this.commonService.notification('An archive build is already in progress', 'danger');
    this.loadArchives();
  }

  downloadArchive(): void {
    if (!this.latest?.id) {
      return;
    }

    this.downloading = true;
    this.projectsService.getProjectArchiveDownloadUrl(this.projectId, this.latest.id)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: { url: string }) => {
          this.downloading = false;
          if (resp?.url) {
            const a = document.createElement('a');
            a.href = resp.url;
            a.click();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.downloading = false;
          if (error?.status === 410) {
            this.commonService.notification('Archive file has expired — please rebuild.', 'danger');
            this.loadArchives();
          } else if (error?.status === 409) {
            this.commonService.notification('Archive is not ready yet — refreshing status', 'danger');
            this.loadArchives();
          } else {
            this.commonService.notification(error?.error?.error || 'Failed to download archive', 'danger');
          }
        },
      });
  }

  private loadArchives(): void {
    this.projectsService.getProjectArchives(this.projectId)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (archives: any[]) => {
          const previousStatus = this.latest?.status;
          this.archives = archives || [];
          this.latest = this.archives[0] || null;
          this.loading = false;

          if (IN_PROGRESS_STATUSES.includes(previousStatus) && this.latest?.status === 'ready') {
            this.commonService.notification('Project archive is ready', 'success');
          }

          if (IN_PROGRESS_STATUSES.includes(this.latest?.status)) {
            this.startPolling();
          } else {
            this.stopPolling();
          }
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private startPolling(): void {
    if (this.pollSub) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      // timer(0, ms), not interval(ms) — interval() waits a full period before its
      // first tick, so a build that finishes inside that window (common for smaller
      // archives) would show nothing new until the next 30s poll or a manual refresh.
      this.pollSub = timer(0, POLL_INTERVAL_MS)
        .pipe(takeUntil(this.onDestroy))
        .subscribe(() => {
          this.zone.run(() => this.loadArchives());
        });
    });
  }

  private stopPolling(): void {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
      this.pollSub = null;
    }
  }
}
